import React, { useEffect, useState } from "react";
import { Gavel, Printer, FileText } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { StatusBadge } from "../../components/StatusBadge";
import { printDoc } from "../../lib/printDoc";
import { InvoiceDoc } from "../../components/docs";
import { AuctionPrep } from "../../components/AuctionPrep";
import { SaleAnnouncement } from "../../components/SaleAnnouncement";
import { SaleResults } from "../../components/SaleResults";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function Auctions() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const [items, setItems] = useState(null);
  const [selling, setSelling] = useState(null);
  const [price, setPrice] = useState("");
  const [buyer, setBuyer] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/shop/auctions").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (!items) return <p className="text-slate-500">{t("common.loading")}</p>;

  const inAuction = items.filter((i) => i.status === "auction");
  const sold = items.filter((i) => i.status === "sold");

  const openInvoice = async (itemId) => {
    try {
      const { data } = await API.get(`/shop/items/${itemId}/auction-invoice`);
      setInvoice(data);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const sell = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post(`/shop/items/${selling.id}/sell`, { sale_price: Number(price), buyer_name: buyer || undefined });
      toast.success(t("msg.sold"));
      const soldId = selling.id;
      setSelling(null);
      setPrice("");
      setBuyer("");
      load();
      openInvoice(soldId);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="auctions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3">
          <Gavel className="h-7 w-7 text-purple-600" />{t("auctions.title")}
        </h1>
        <div className="flex gap-2 flex-wrap">
          <SaleResults />
          <SaleAnnouncement managerUp={managerUp} onAwarded={(id) => { load(); openInvoice(id); }} />
        </div>
      </div>

      <AuctionPrep managerUp={managerUp} onChanged={load} />

      <section>
        <h2 className="font-heading text-lg font-semibold text-purple-700 mb-4">
          {t("auctions.in_auction")} ({inAuction.length})
        </h2>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("items.category")}</TableHead>
                <TableHead>{t("items.loan")}</TableHead>
                <TableHead>{t("loans.customer")}</TableHead>
                <TableHead>{t("items.est_value")}</TableHead>
                <TableHead>{t("auctions.days_listed")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inAuction.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">{t("auctions.empty")}</TableCell></TableRow>
              )}
              {inAuction.map((i) => (
                <TableRow key={i.id} data-testid={`auction-row-${i.id.slice(0, 8)}`}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell>{i.category}</TableCell>
                  <TableCell className="font-mono text-sm">{i.loan_number || "—"}</TableCell>
                  <TableCell>{i.customer_name || "—"}</TableCell>
                  <TableCell className="font-mono">{money(i.estimated_value)}</TableCell>
                  <TableCell>{i.days_listed ?? 0}</TableCell>
                  <TableCell className="text-right">
                    {managerUp && (
                      <Button size="sm" data-testid={`sell-item-${i.id.slice(0, 8)}`}
                        onClick={() => { setSelling(i); setPrice(String(i.estimated_value || "")); }}
                        className="bg-purple-600 hover:bg-purple-700 text-white">
                        {t("auctions.mark_sold")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {sold.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-semibold text-teal-700 mb-4">
            {t("auctions.sold")} ({sold.length})
          </h2>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("items.loan")}</TableHead>
                  <TableHead>{t("auctions.sold_for")}</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sold.map((i) => (
                  <TableRow key={i.id} data-testid={`sold-row-${i.id.slice(0, 8)}`}>
                    <TableCell className="font-medium">{i.name}</TableCell>
                    <TableCell className="font-mono text-sm">{i.loan_number || "—"}</TableCell>
                    <TableCell className="font-mono font-semibold text-teal-700">{money(i.sale_price)}</TableCell>
                    <TableCell>{fmtDate(i.sold_at)}</TableCell>
                    <TableCell><StatusBadge status="sold" /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" data-testid={`invoice-button-${i.id.slice(0, 8)}`}
                        onClick={() => openInvoice(i.id)}>
                        <FileText className="h-4 w-4 mr-1" />{t("auctions.view_invoice")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      <Dialog open={!!selling} onOpenChange={(o) => !o && setSelling(null)}>
        <DialogContent className="bg-white max-w-sm" data-testid="sell-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("auctions.mark_sold")} — {selling?.name}</DialogTitle></DialogHeader>
          <form onSubmit={sell} className="space-y-4">
            <div>
              <Label>{t("auctions.sale_price")}</Label>
              <Input data-testid="sale-price-input" type="number" min="0.01" step="0.01" required value={price}
                onChange={(e) => setPrice(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>{t("auctions.buyer_name")}</Label>
              <Input data-testid="sale-buyer-input" value={buyer}
                onChange={(e) => setBuyer(e.target.value)} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSelling(null)}>{t("common.cancel")}</Button>
              <Button data-testid="sell-confirm-button" type="submit" disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white">{t("common.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invoice} onOpenChange={(o) => !o && setInvoice(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("inv.title")}</DialogTitle></DialogHeader>
          {invoice && <InvoiceDoc invoice={invoice} />}
          <div className="flex gap-2 mt-4">
            <Button data-testid="invoice-print-a4-button"
              onClick={() => printDoc(document.querySelector('[data-testid="invoice-body"]'), { mode: "a4", title: invoice?.sale?.invoice_no || "INV" })}
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
              <Printer className="h-4 w-4 mr-1.5" />{t("repay.print")} A4
            </Button>
            <Button data-testid="invoice-print-thermal-button" variant="outline"
              onClick={() => printDoc(document.querySelector('[data-testid="invoice-body"]'), { mode: "thermal", title: invoice?.sale?.invoice_no || "INV" })}
              className="flex-1">
              <Printer className="h-4 w-4 mr-1.5" />80mm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
